"use client";

import { format, subMonths } from "date-fns";
import { PlusCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import * as React from "react";

import { Spinner } from "@/components/icons/spinner";
import { LoginLink } from "@/components/login-link";
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { fetchWrapper } from "@/lib/hooks/fetch-client";
import { cn } from "@/lib/utils";
import { filterRFCategories } from "@/lib/utils/research-feed";
import { Category, Response, User } from "@/types";

interface ResearchFeedArchivesProps extends React.HTMLAttributes<HTMLDivElement> {
	user?: User;
}

const ResearchFeedArchives = ({
	children,
	className,
	user,
	...props
}: ResearchFeedArchivesProps) => {
	const searchParams = useSearchParams();
	const date = searchParams.get("date");
	const category = searchParams.get("category");
	const dialogRef = React.useRef<HTMLDivElement>(null);
	const [loading, setLoading] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	const [categories, setCategories] = React.useState([]);
	const [issues, setIssues] = React.useState([]);

	const generateIssuesRes = () => {
		// Get the current month and year
		const currentDate = new Date();

		// Create an array to store the months and slugs
		const months = [];

		// Iterate over the months starting from the current month
		let date = currentDate;
		while (date >= new Date(2020, 6, 1)) {
			// Stop when reaching July 2020
			const month = date.getMonth();
			const year = date.getFullYear();

			// Exclude August 2022
			if (month === 7 && year === 2022) {
				date = subMonths(date, 1);
				continue;
			}

			// Get the month name and format it
			const monthName = format(date, "MMMM");
			const slug = `${monthName.toLowerCase()}-${year}`;

			// Create an object for the month and slug
			const monthSlug = {
				title: `${monthName} ${year}`,
				slug: slug,
			};

			// Push the object to the array
			months.push(monthSlug);

			// Move to the previous month
			date = subMonths(date, 1);
		}

		return months;
	};

	React.useEffect(() => {
		if (!open || categories?.length) return;

		(async () => {
			setLoading(true);
			try {
				const categories: Response<Category[]> = await fetchWrapper(
					`/v1/study-summaries/categories`
				);

				if (categories?.data) {
					const filteredCategories = filterRFCategories(categories.data);
					setCategories(filteredCategories);
				}
				setIssues(generateIssuesRes());
			} catch (err) {
				// console.error(err);
			} finally {
				setLoading(false);
			}
		})();
	}, [open, categories]);

	return (
		<Sheet
			onOpenChange={(e) => {
				setOpen(e);
			}}
		>
			<SheetTrigger className="w-full" asChild>
				{children}
			</SheetTrigger>
			<SheetContent
				side={"right"}
				className="overflow-x-hidden pt-0"
				onClick={(e) => e.stopPropagation()}
				ref={dialogRef}
			>
				{user ? (
					<>
						<SheetHeader className="sticky top-0 mx-auto flex flex-row items-center justify-between space-y-0 bg-white pt-6">
							<SheetTitle>Previous summaries</SheetTitle>
							<SheetClose className="flex rotate-45 items-center">
								<PlusCircle />
							</SheetClose>
						</SheetHeader>
						{loading ? (
							<div className="flex h-full items-center justify-center">
								<Spinner />
							</div>
						) : (
							<section className={cn("w-full space-y-6 pt-4")} {...props}>
								<div className="mx-auto w-full max-w-md">
									<h3 className="text-lg font-bold leading-8 -tracking-2 text-examine-purple-500">
										Archives by Month
									</h3>

									<div className="columns-2 gap-5 py-8">
										{issues?.map((item) => (
											<Link
												key={item.slug}
												href={`/research-feed/filter/?filter=archive&date=${item.slug}`}
												className={cn(
													"block cursor-pointer py-2 hover:text-examine-purple-400 hover:underline",
													date === item.slug ? "font-bold" : ""
												)}
											>
												{item.title}
											</Link>
										))}
									</div>
								</div>

								<div className="mx-auto w-full max-w-md">
									<h3 className="text-lg font-bold leading-8 -tracking-2 text-examine-purple-500">
										Archives by Categories
									</h3>

									<div className="grid grid-cols-3 gap-6 py-4">
										{categories?.map(({ name, icon, slug }) => (
											<Link
												key={slug}
												href={`/research-feed/filter/?filter=archive&category=${slug}`}
											>
												<div
													className={cn(
														"peer grid h-30 cursor-pointer place-content-center rounded-t-lg bg-examine-green-500/10 hover:scale-110",
														category === slug ? "scale-105 border-2 border-gray-400" : ""
													)}
												>
													<Image
														src={icon}
														alt={slug}
														width={100}
														height={100}
														className="rounded-lg"
													/>
												</div>
												<p className="mt-1.5 text-xs leading-normal peer-hover:text-examine-purple-400 peer-hover:underline">
													{name}
												</p>
											</Link>
										))}
									</div>
								</div>
							</section>
						)}
					</>
				) : (
					<div className="flex h-full flex-col items-center justify-center text-gray-900">
						<p className="overflow-hidden rounded-xl border-l-8 border-examine-purple-300 bg-gray-100  px-9 py-2 pb-4 pt-2 leading-8 text-gray-900">
							<LoginLink className="text-primary hover:text-examine-purple-400 hover:underline">
								Log in
							</LoginLink>{" "}
							or{" "}
							<Link
								href="/signup/"
								className="text-primary hover:text-examine-purple-400 hover:underline"
							>
								Sign up
							</Link>{" "}
							to access Research Feed archive.
						</p>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
};

export { ResearchFeedArchives };
